/**
 * Toebehoren ordenen voor de productpagina.
 *
 * Timmers levert ze als een platte lijst, en bij een glasklem staan er dan negen regels onder
 * elkaar: "Pakkingset voor 6mm", "Pakkingset voor 33.2", "Pakkingset voor 8mm"… Dat leest als
 * negen losse producten terwijl het één vraag is — welke glasdikte heeft u? — met één antwoord.
 * En "33.2" zegt een particulier niets.
 *
 * Deze module maakt er twee dingen van: families (zelfde naam, andere maat → één keuze) en losse
 * toebehoren (een zekeringsstift, een kitpistool → een gewoon vinkje).
 */

export interface ToebehorenItem {
  slug: string;
  name: string;
  priceCents: number;
  perStuk?: number;
  standaardAan?: boolean;
  inStock?: boolean;
}

export interface ToebehorenFamilie {
  /** "Pakkingset" — de naam zonder de maat. */
  titel: string;
  /** De keuzes, in de volgorde waarin Timmers ze aanlevert. */
  opties: (ToebehorenItem & { maat: string; toelichting?: string })[];
}

export interface ToebehorenIndeling {
  families: ToebehorenFamilie[];
  los: ToebehorenItem[];
}

/**
 * Gelaagde opbouwcodes naar een begrijpelijke dikte.
 *
 * "33.2" = twee ruiten van 3 mm met 2 folies ertussen; dat is samen ongeveer 6,8 mm. Een klant
 * met gelaagd glas kent zijn code, een klant met blank glas kent zijn millimeters — door allebei
 * te tonen hoeft niemand te gokken welke pakking past.
 */
const GELAAGD: Record<string, string> = {
  "33.2": "gelaagd, ± 6,8 mm",
  "44.2": "gelaagd, ± 8,8 mm",
  "55.2": "gelaagd, ± 10,8 mm",
  "55.4": "gelaagd, ± 11,5 mm",
  "66.2": "gelaagd, ± 12,8 mm",
};

/** "Pakkingset voor 33.2" → { basis: "Pakkingset", maat: "33.2" }; geen "voor" → null. */
function splits(naam: string): { basis: string; maat: string } | null {
  const m = /^(.+?)\s+voor\s+(.+)$/i.exec(naam.trim());
  if (!m) return null;
  const basis = m[1]!.trim();
  const maat = m[2]!.trim();
  return basis && maat ? { basis, maat } : null;
}

/**
 * Deelt de toebehoren op in families en losse regels.
 *
 * Een familie ontstaat pas bij TWEE of meer regels met dezelfde basisnaam — één "Pakkingset voor
 * 6 & 10mm" bij een scharnier blijft dus gewoon een losse regel, want daar valt niets te kiezen.
 */
export function deelToebehorenIn(extras: readonly ToebehorenItem[]): ToebehorenIndeling {
  const perBasis = new Map<string, (ToebehorenItem & { maat: string })[]>();
  const los: ToebehorenItem[] = [];
  const volgorde: string[] = [];

  for (const e of extras) {
    const s = splits(e.name);
    if (!s) { los.push(e); continue; }
    if (!perBasis.has(s.basis)) { perBasis.set(s.basis, []); volgorde.push(s.basis); }
    perBasis.get(s.basis)!.push({ ...e, maat: s.maat });
  }

  const families: ToebehorenFamilie[] = [];
  for (const basis of volgorde) {
    const leden = perBasis.get(basis)!;
    if (leden.length < 2) { los.push(...leden.map(({ maat: _maat, ...rest }) => rest)); continue; }
    families.push({
      titel: basis,
      opties: leden.map((l) => ({ ...l, ...(GELAAGD[l.maat] ? { toelichting: GELAAGD[l.maat] } : {}) })),
    });
  }
  return { families, los };
}
